import { useEffect, useRef, type ChangeEvent } from "react";
import { Cross2Icon } from "@radix-ui/react-icons";
import { CameraIcon } from "./CameraIcon";

type Props = {
  open: boolean;
  busy: boolean;
  error: string;
  previewUrl: string;
  onClose: () => void;
  onRetry?: () => void;
  onSelect: (file: File) => void;
  returnFocus: HTMLElement | null;
};

export function ImageCaptureGuide({open,busy,error,previewUrl,onClose,onRetry,onSelect,returnFocus}:Props) {
  const dialog=useRef<HTMLElement>(null);
  const closeButton=useRef<HTMLButtonElement>(null);
  useEffect(()=>{
    if(!open)return;
    closeButton.current?.focus({preventScroll:true});
    const keydown=(event:KeyboardEvent)=>{
      if(event.key==="Escape"&&!busy)onClose();
      if(event.key!=="Tab"||!dialog.current)return;
      const nodes=[...dialog.current.querySelectorAll<HTMLElement>('button:not(:disabled),label:not(.disabled)')];
      if(!nodes.length)return;
      const first=nodes[0],last=nodes[nodes.length-1];
      if(event.shiftKey&&document.activeElement===first){event.preventDefault();last.focus();}
      else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus();}
    };
    window.addEventListener("keydown",keydown);
    return()=>{window.removeEventListener("keydown",keydown);returnFocus?.focus({preventScroll:true});};
  },[busy,onClose,open,returnFocus]);
  if(!open)return null;
  const select=(event:ChangeEvent<HTMLInputElement>)=>{const file=event.target.files?.[0];event.target.value="";if(file)onSelect(file);};
  const picker=(label:string,capture=false)=><label className={busy?"disabled":""} role="button" tabIndex={busy?-1:0} onKeyDown={event=>{if(event.key==='Enter'||event.key===' '){event.preventDefault();event.currentTarget.click();}}}>{capture?<CameraIcon/>:null}{label}<input tabIndex={-1} type="file" accept="image/jpeg,image/png,image/webp" capture={capture?"environment":undefined} disabled={busy} onChange={select}/></label>;
  const failed=Boolean(error);
  return <div className="capture-guide-layer" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget&&!busy)onClose();}}>
    <section ref={dialog} className={`capture-guide${failed?' capture-guide-failed':''}`} role="dialog" aria-modal="true" aria-labelledby="capture-guide-title" aria-describedby={failed?'capture-guide-copy':'capture-guide-copy capture-guide-secondary'}>
      <button ref={closeButton} className="capture-guide-close" type="button" disabled={busy} aria-label="关闭拍摄提示" onClick={onClose}><Cross2Icon/></button>
      {!failed?<div className="capture-guide-visual"><img src="/assets/camera-guide.png" alt="香水瓶正面拍摄示意，标出品牌和款名位置"/></div>:previewUrl?<img className="capture-selected-preview" src={previewUrl} alt="本次选择的照片"/>:null}
      <h2 id="capture-guide-title">{failed?'这次没识别完成':'拍清瓶身正面'}</h2>
      <p id="capture-guide-copy" role={failed?'alert':undefined}>{failed?error:'品牌和款名越清楚，越容易找到。'}</p>
      {!failed?<p id="capture-guide-secondary" className="capture-guide-secondary">避开反光与遮挡，也可以上传商品截图。</p>:null}
      {busy?<p className="capture-guide-status" role="status">正在处理照片，随后会自动识别…</p>:null}
      <div className="capture-guide-actions">{failed&&onRetry?<button type="button" disabled={busy} onClick={onRetry}>重试识别</button>:picker('拍照',true)}{picker(failed?'换一张照片':'从相册选择')}</div>
    </section>
  </div>;
}
